<template>
  <footer class="site-footer">
    <div class="footer-container" :class="{ 'footer-container--single': isPlatform }">
      <div v-if="showContact" class="footer-col">
        <h3 class="footer-title">Contacto</h3>
        <ul class="footer-list">
          <li>
            <a :href="`tel:${phoneHref}`" class="footer-link" :title="$t('footer.phoneTitle')">
              <i class="fas fa-phone"></i>
              <span>{{ phoneDisplay }}</span>
            </a>
          </li>
          <li>
            <a
              :href="whatsappUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="footer-link whatsapp"
              :title="$t('footer.whatsappTitle')"
            >
              <i class="fab fa-whatsapp"></i>
              <span>WhatsApp</span>
            </a>
          </li>
          <li class="footer-link">
            <i class="fas fa-map-marker-alt"></i>
            <span>{{ address }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="footer-bottom">
      <p class="footer-copy">&copy; {{ year }} · {{ $t('footer.rights') }}</p>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getWhatsAppLink } from '@/utils/whatsapp';
import { loadBranding } from '@/services/branding.service';

const { t: $t } = useI18n();

const branding = computed(() => loadBranding());

const isPlatform = computed(() => branding.value.isPlatform);

// Contact data is per-host: brand subdomains get the clinic phone/address from
// the bootstrap payload, the platform apex has none and hides the block.
const showContact = computed(
  () => !isPlatform.value && Boolean(branding.value.phone || branding.value.address),
);

const phoneRaw = computed(() => branding.value.phone || '');
const phoneDisplay = computed(() => (branding.value.phone || '').replace(/^\+56/, '+56 '));
const phoneHref = computed(() => phoneRaw.value);
const address = computed(() => branding.value.address || '');
const year = new Date().getFullYear();

const whatsappUrl = computed(() =>
  getWhatsAppLink(phoneRaw.value, `Hola, te contacto desde ${branding.value.siteName}.`),
);
</script>

<style scoped>
.site-footer {
  background: linear-gradient(160deg, #1a0035 0%, #2d0066 60%, #4a148c 100%);
  color: #eae0f0;
  padding: 3rem 1.25rem 1.5rem;
  width: 100%;
  box-sizing: border-box;
  margin-top: auto;
}

.footer-container {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 2.5rem;
  align-items: start;
}

.footer-container--single {
  grid-template-columns: 1fr;
}

.footer-col {
  min-width: 0;
}

.footer-title {
  color: #d8b4fe;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.footer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.footer-link {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: #eae0f0;
  text-decoration: none;
  font-size: 0.95rem;
  flex-wrap: wrap;
}

.footer-link i {
  width: 1.1rem;
  text-align: center;
  color: #c084fc;
}

a.footer-link:hover {
  color: #ffffff;
}

.footer-link.whatsapp {
  color: #25d366;
  font-weight: 600;
}

.footer-link.whatsapp i {
  color: #25d366;
}

.footer-bottom {
  max-width: 1100px;
  margin: 2rem auto 0;
  border-top: 1px solid rgba(216, 180, 254, 0.15);
  padding-top: 1.25rem;
  text-align: center;
}

.footer-copy {
  margin: 0;
  font-size: 0.8rem;
  opacity: 0.7;
}

@media (max-width: 720px) {
  .footer-container {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}
</style>
